import { useEffect } from 'react';
import { useForm, Controller } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { XMarkIcon } from '@heroicons/react/24/outline';
import { useInviteExternalUser } from '../../hooks/useExternalUsers';
import { useEscapeHandler } from '../../hooks/useEscapeHandler';
import { ApiClientError } from '../../services/api/client';
import type { InviteInput } from '../../services/api/externalUsers';
import { inviteExternalUserSchema, type InviteExternalUserInput } from './_schemas';
import { ProjectPicker } from './ProjectPicker';

/**
 * Invite-external-user modal.
 *
 * Form fields: First name, Last name, Email address (lowercased on submit), Projects (multi-select).
 * Uses `useInviteExternalUser` mutation (15-01) which invalidates `['external-users']` on success.
 *
 * Server-error mapping note: `services/api/client.ts` ApiClientError preserves only
 * `statusCode`, `message`, and `details`. The backend throws `new ConflictError('EMAIL_ALREADY_EXISTS')`
 * etc., which serializes as `{ message: 'EMAIL_ALREADY_EXISTS' }` — so the frontend keys server
 * errors off `err.message` (NOT a non-existent `err.errorCode`). See 15-03-SUMMARY deviation note.
 */

export interface InviteExternalUserModalProps {
  isOpen: boolean;
  onClose: () => void;
}

function mapInviteServerError(err: unknown): string | null {
  if (!(err instanceof ApiClientError)) return null;
  if (err.message === 'EMAIL_ALREADY_EXISTS') {
    return 'An account with this email already exists.';
  }
  if (err.message === 'INVALID_PROJECT') {
    return 'One or more selected projects are no longer available. Please refresh and try again.';
  }
  return 'Something went wrong, please try again.';
}

export function InviteExternalUserModal({ isOpen, onClose }: InviteExternalUserModalProps) {
  const mutation = useInviteExternalUser();
  const {
    register,
    handleSubmit,
    control,
    reset,
    formState: { errors, isDirty },
  } = useForm<InviteExternalUserInput>({
    resolver: zodResolver(inviteExternalUserSchema),
    defaultValues: { firstName: '', lastName: '', email: '', projectIds: [] },
  });

  // Reset the form whenever the modal is reopened — also clears any prior server error
  // by resetting mutation state via TanStack Query's `reset()`.
  useEffect(() => {
    if (isOpen) {
      reset({ firstName: '', lastName: '', email: '', projectIds: [] });
      mutation.reset();
    }
    // We intentionally exclude `mutation` from deps — it's a stable mutation handle but its
    // identity changes on every render, which would cause a reset loop.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen, reset]);

  const tryClose = () => {
    if (isDirty && !window.confirm('Discard invite?')) return;
    onClose();
  };

  // Escape key with dirty-confirm gating.
  useEscapeHandler(isOpen, tryClose);

  if (!isOpen) return null;

  const onSubmit = handleSubmit(async (values) => {
    const payload: InviteInput = {
      email: values.email,
      firstName: values.firstName,
      lastName: values.lastName,
      projectIds: values.projectIds,
    };
    try {
      await mutation.mutateAsync(payload);
      onClose();
    } catch {
      // Error state is rendered from `mutation.isError` / `mutation.error` below.
    }
  });

  const handleBackdropClick = (e: React.MouseEvent<HTMLDivElement>) => {
    if (e.target !== e.currentTarget) return;
    if (isDirty) return; // backdrop ignored when form is dirty (T-15-03-03)
    onClose();
  };

  const serverError = mutation.isError ? mapInviteServerError(mutation.error) : null;

  return (
    <div
      role="presentation"
      onClick={handleBackdropClick}
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4"
    >
      <div
        role="dialog"
        aria-labelledby="invite-external-user-title"
        aria-modal="true"
        className="bg-white rounded-lg shadow-xl w-full max-w-lg p-6"
      >
        <div className="flex items-center justify-between mb-4">
          <h2
            id="invite-external-user-title"
            className="text-lg font-semibold text-gray-900"
          >
            Invite external user
          </h2>
          <button
            type="button"
            onClick={tryClose}
            aria-label="Close"
            className="text-gray-400 hover:text-gray-600"
          >
            <XMarkIcon className="h-5 w-5" />
          </button>
        </div>

        {serverError && (
          <div
            role="alert"
            className="mb-3 rounded-md bg-red-50 border border-red-200 p-3 text-sm text-red-800"
          >
            {serverError}
          </div>
        )}

        <form onSubmit={onSubmit} className="space-y-4" noValidate>
          <div>
            <label
              htmlFor="invite-first-name"
              className="block text-sm font-medium text-gray-700"
            >
              First name
            </label>
            <input
              id="invite-first-name"
              type="text"
              autoComplete="given-name"
              {...register('firstName')}
              className="mt-1 w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
            />
            {errors.firstName && (
              <p role="alert" className="mt-1 text-sm text-red-600">
                {errors.firstName.message}
              </p>
            )}
          </div>

          <div>
            <label
              htmlFor="invite-last-name"
              className="block text-sm font-medium text-gray-700"
            >
              Last name
            </label>
            <input
              id="invite-last-name"
              type="text"
              autoComplete="family-name"
              {...register('lastName')}
              className="mt-1 w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
            />
            {errors.lastName && (
              <p role="alert" className="mt-1 text-sm text-red-600">
                {errors.lastName.message}
              </p>
            )}
          </div>

          <div>
            <label
              htmlFor="invite-email"
              className="block text-sm font-medium text-gray-700"
            >
              Email address
            </label>
            <input
              id="invite-email"
              type="email"
              autoComplete="email"
              {...register('email')}
              className="mt-1 w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
            />
            {errors.email && (
              <p role="alert" className="mt-1 text-sm text-red-600">
                {errors.email.message}
              </p>
            )}
          </div>

          <Controller
            control={control}
            name="projectIds"
            render={({ field }) => (
              <ProjectPicker
                value={field.value}
                onChange={field.onChange}
                error={errors.projectIds?.message}
                disabled={mutation.isPending}
              />
            )}
          />

          <div className="flex justify-end gap-2 pt-2">
            <button
              type="button"
              onClick={tryClose}
              disabled={mutation.isPending}
              className="px-4 py-2 rounded-md border border-gray-300 text-gray-700 hover:bg-gray-50 disabled:opacity-50"
            >
              {isDirty ? 'Discard invite' : 'Cancel'}
            </button>
            <button
              type="submit"
              disabled={mutation.isPending}
              className="px-4 py-2 rounded-md bg-indigo-600 text-white hover:bg-indigo-700 disabled:opacity-50"
            >
              {mutation.isPending ? 'Inviting…' : 'Invite'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
